import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner
} from 'react-bootstrap';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  serverTimestamp,
  GeoPoint
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import './AddListingModal.css';

// Helper: geocode via Google Maps Geocoding API
async function geocodeAddress(address) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status === 'OK' && data.results.length > 0) {
    const loc = data.results[0].geometry.location;
    return { lat: loc.lat, lng: loc.lng };
  }
  throw new Error('Geocoding failed');
}

export default function AddListingModal({ show, onClose }) {
  const auth = getAuth();
  const db = getFirestore();
  const storage = getStorage();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [stateField, setStateField] = useState('');
  const [price, setPrice] = useState('');

  const [images, setImages] = useState([null, null, null]);
  const [previews, setPreviews] = useState(['', '', '']);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setPreviews(images.map(f => (f ? URL.createObjectURL(f) : '')));
  }, [images]);

  const isDisabled =
    !title.trim() ||
    !description.trim() ||
    !city.trim() ||
    !stateField.trim() ||
    !price.trim() ||
    images.every(i => !i);

  const handleFileChange = (e, i) => {
    const file = e.target.files[0] || null;
    setImages(arr => {
      const c = [...arr]; c[i] = file; return c;
    });
  };
  const handleRemoveImage = i => {
    setImages(arr => {
      const c = [...arr]; c[i] = null; return c;
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (isDisabled) return;
    setSaving(true);
    setError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const id = doc(collection(db, 'forsale')).id;
      const address = `${city}, ${stateField} ${zipCode}`;
      let lat = 0, lng = 0;
      try { ({ lat, lng } = await geocodeAddress(address)); } catch {}
      const urls = await Promise.all(images.map((f,i) => {
        if (!f) return Promise.resolve(null);
        const p = `forSaleImages/${user.uid}/${id}_${i+1}.jpg`;
        const r = storageRef(storage, p);
        return uploadBytes(r, f).then(() => getDownloadURL(r));
      }));
      const data = {
        forsaleId: id,
        receiverId: user.uid,
        status: 'unverified',
        reason: '',
        title: title.trim(),
        description: description.trim(),
        city: city.trim(),
        zipCode: zipCode.trim(),
        state: stateField.trim(),
        price: price.trim(),
        timestamp: serverTimestamp(),
        listingLocation: new GeoPoint(lat, lng)
      };
      urls.forEach((u,i) => u && (data[`image${i+1}`] = u));
      await setDoc(doc(db, 'forsale', id), data);
      onClose();
      setTitle(''); setDescription(''); setCity('');
      setZipCode(''); setStateField(''); setPrice('');
      setImages([null,null,null]);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error saving listing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      scrollable
      dialogClassName="add-listing-dialog modal-dialog-scrollable"
      contentClassName="add-listing-content"
    >
      <Modal.Header closeButton>
        <Modal.Title>New Listing</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>
          <Row className="mb-3">
            {images.map((_, i) => (
              <Col key={i} xs={4} className="position-relative">
                {previews[i] ? (
                  <img src={previews[i]} className="img-preview" alt="" />
                ) : (
                  <div className="img-placeholder">+</div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="file-input"
                  onChange={e => handleFileChange(e, i)}
                />
                {previews[i] && (
                  <Button
                    variant="light"
                    size="sm"
                    className="remove-btn"
                    onClick={() => handleRemoveImage(i)}
                  >&times;</Button>
                )}
              </Col>
            ))}
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={50}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={200}
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>City</Form.Label>
                <Form.Control
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  maxLength={25}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>State</Form.Label>
                <Form.Control
                  value={stateField}
                  onChange={e => setStateField(e.target.value)}
                  maxLength={10}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Zip Code</Form.Label>
                <Form.Control
                  value={zipCode}
                  onChange={e => setZipCode(e.target.value)}
                  maxLength={10}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Price</Form.Label>
            <Form.Control
              value={price}
              onChange={e => setPrice(e.target.value)}
              maxLength={10}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isDisabled || saving}
        >
          {saving ? <Spinner animation="border" size="sm" /> : 'Post Now'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
